import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  TagIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { Textarea } from '../../components/common/Textarea';
import { Checkbox } from '../../components/common/Checkbox';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  PimAttributeGroup, 
  PimAttributeGroupFilters, 
  PimAttributeGroupFormData 
} from '../../types/pim';

export const AttributeGroups = () => {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<PimAttributeGroupFilters>({
    page: 1,
    limit: 20,
    sort_by: 'sort_order',
    sort_direction: 'asc'
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<PimAttributeGroup | null>(null);
  const [formData, setFormData] = useState<PimAttributeGroupFormData>({
    name: '',
    code: '',
    description: '',
    sortOrder: 1,
    isGlobal: true,
    productTypeId: undefined
  });

  // Fetch attribute groups
  const { data: groupsData, isLoading: loadingGroups, error: groupsError } = useQuery({
    queryKey: ['pim-attribute-groups', filters],
    queryFn: () => pimService.getAttributeGroups(filters),
    enabled: permissions.canReadProducts,
    keepPreviousData: true
  });

  // Fetch product types for dropdown
  const { data: productTypesData } = useQuery({
    queryKey: ['pim-product-types'],
    queryFn: () => pimService.getProductTypesForDropdown(),
    enabled: permissions.canReadProducts
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: PimAttributeGroupFormData) => pimService.createAttributeGroup(data),
    onSuccess: () => {
      showNotification('Grupa atrybutów została pomyślnie utworzona', 'success');
      queryClient.invalidateQueries(['pim-attribute-groups']);
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas tworzenia grupy atrybutów', 'error');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PimAttributeGroupFormData> }) => 
      pimService.updateAttributeGroup(id, data),
    onSuccess: () => {
      showNotification('Grupa atrybutów została pomyślnie zaktualizowana', 'success');
      queryClient.invalidateQueries(['pim-attribute-groups']);
      setShowEditModal(false);
      resetForm();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas aktualizacji grupy atrybutów', 'error');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => pimService.deleteAttributeGroup(id),
    onSuccess: () => {
      showNotification('Grupa atrybutów została pomyślnie usunięta', 'success');
      queryClient.invalidateQueries(['pim-attribute-groups']);
      setShowDeleteModal(false);
      setSelectedGroup(null);
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas usuwania grupy atrybutów', 'error');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      sortOrder: 1,
      isGlobal: true,
      productTypeId: undefined
    });
    setSelectedGroup(null);
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value || undefined,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof PimAttributeGroupFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleSort = (sortBy: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: sortBy,
      sort_direction: prev.sort_by === sortBy && prev.sort_direction === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  const handleCreate = () => {
    setShowCreateModal(true);
    resetForm();
  };

  const handleEdit = (group: PimAttributeGroup) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name || '',
      code: group.code || '',
      description: group.description || '',
      sortOrder: group.sort_order || 1,
      isGlobal: Boolean(group.is_global),
      productTypeId: group.product_type_id
    });
    setShowEditModal(true);
  };

  const handleDelete = (group: PimAttributeGroup) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const handleSubmit = () => {
    if (showEditModal && selectedGroup) {
      updateMutation.mutate({ id: selectedGroup.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sort_by: 'sort_order',
      sort_direction: 'asc'
    });
  };

  // Auto-generate code from name
  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      code: name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    }));
  };

  if (!permissions.canReadProducts) {
    return (
      <div className="text-center py-12">
        <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.common.accessDenied')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('pim.common.accessDeniedDescription')}
        </p>
      </div>
    );
  }

  if (groupsError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading attribute groups: {(groupsError as any).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pim.attributeGroups.title')}</h1>
          <p className="text-gray-600">{t('pim.attributeGroups.subtitle')}</p>
        </div>
        {permissions.canManagePim && (
          <Button icon={PlusIcon} onClick={handleCreate}>
            {t('pim.attributeGroups.addGroup')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t('pim.attributeGroups.searchPlaceholder')}
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                icon={MagnifyingGlassIcon}
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={filters.is_global?.toString() || ''}
                onChange={(e) => handleFilterChange('is_global', e.target.value ? e.target.value === 'true' : undefined)}
                options={[
                  { value: '', label: t('pim.common.allTypes') },
                  { value: 'true', label: t('pim.attributeGroups.globalGroups') || 'Global Groups' },
                  { value: 'false', label: t('pim.attributeGroups.productTypeGroups') || 'Product Type Groups' }
                ]}
              />
              {(filters.search || filters.is_global !== undefined) && (
                <Button
                  variant="outline"
                  icon={XMarkIcon}
                  onClick={clearFilters}
                >
                  {t('pim.attributeGroups.clearFilters')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Groups Table */}
      <Card>
        <CardContent>
          {loadingGroups ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">{t('pim.attributeGroups.loading')}</p>
            </div>
          ) : !groupsData?.data?.length ? (
            <div className="text-center py-12">
              <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.attributeGroups.noGroups')}</h3>
              <p className="mt-1 text-sm text-gray-500">{t('pim.attributeGroups.noGroupsDescription')}</p>
              {permissions.canManagePim && (
                <div className="mt-6">
                  <Button icon={PlusIcon} onClick={handleCreate}>
                    {t('pim.attributeGroups.addGroup')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('sort_order')}
                    >
                      {t('pim.attributeGroups.form.sortOrder')}
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      {t('pim.attributeGroups.table.name')}
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('code')}
                    >
                      {t('pim.attributeGroups.code')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributeGroups.table.description')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributeGroups.productTypeColumn')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributeGroups.typeColumn')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributeGroups.table.attributes')}
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at')}
                    >
                      {t('pim.attributeGroups.created')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributeGroups.table.actions') || t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupsData.data.map((group) => (
                    <tr key={group.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                          {group.sort_order}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{group.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">{group.code}</code>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {group.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {group.productType?.name || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {group.is_global ? (
                          <Badge variant="primary">{t('pim.attributeGroups.global')}</Badge>
                        ) : (
                          <Badge variant="secondary">{t('pim.attributeGroups.productTypeSpecific')}</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {group.attributes?.length || 1}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          if (!group.created_at) return '-';
                          const date = new Date(group.created_at);
                          return isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          {permissions.canManagePim && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={PencilIcon}
                                onClick={() => handleEdit(group)}
                              >
                                {t('common.edit')}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={TrashIcon}
                                onClick={() => handleDelete(group)}
                              >
                                {t('common.delete')}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {groupsData?.pagination && (
            <div className="mt-6">
              <Pagination
                currentPage={groupsData.pagination.page}
                totalPages={groupsData.pagination.totalPages}
                totalItems={groupsData.pagination.total}
                itemsPerPage={groupsData.pagination.limit}
                onPageChange={(page) => handleFilterChange('page', page)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        title={showEditModal ? t('pim.attributeGroups.editGroup') : t('pim.attributeGroups.addGroup')}
      >
        <div className="space-y-4">
          <Input
            label={t('pim.attributeGroups.form.groupName')}
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
            placeholder={t('pim.attributeGroups.form.groupNamePlaceholder')}
          />
          <Input
            label={t('pim.attributeGroups.form.groupCode')}
            value={formData.code}
            onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
            required
            placeholder={t('pim.attributeGroups.form.groupCodePlaceholder')}
            help={t('pim.attributeGroups.code')}
          />
          <Textarea
            label={t('pim.attributeGroups.form.groupDescription')}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder={t('pim.attributeGroups.form.groupDescriptionPlaceholder')}
            rows={3}
          />
          <Select
            label={t('pim.attributeGroups.form.productType')}
            value={formData.productTypeId?.toString() || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, productTypeId: e.target.value ? parseInt(e.target.value) : undefined }))}
          >
            <option value="">{t('pim.attributeGroups.form.productTypePlaceholder')}</option>
            {productTypesData?.data?.map((productType: any) => (
              <option key={productType.id} value={productType.id}>
                {productType.name}
              </option>
            ))}
          </Select>
          <Input
            label={t('pim.attributeGroups.form.sortOrder')}
            type="number"
            value={formData.sortOrder}
            onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 1 }))}
            placeholder={t('pim.attributeGroups.form.sortOrderPlaceholder')}
            help={t('pim.attributeGroups.form.sortOrderHelp')}
          />
          <Checkbox
            checked={formData.isGlobal}
            onChange={(e) => setFormData(prev => ({ ...prev, isGlobal: e.target.checked }))}
            label={t('pim.attributeGroups.form.isGlobalLabel')}
            help={t('pim.attributeGroups.form.isGlobalHelp')}
          />
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                resetForm();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {(createMutation.isLoading || updateMutation.isLoading) ? t('pim.common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedGroup(null);
        }}
        title={t('pim.attributeGroups.deleteGroup')}
      >
        <div className="space-y-4">
          <p>
            {t('pim.attributeGroups.confirmDelete', { name: selectedGroup?.name })}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <XMarkIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {t('common.warning')}
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    {t('pim.attributeGroups.deleteWarning')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedGroup(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => selectedGroup && deleteMutation.mutate(selectedGroup.id)}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? t('pim.common.deleting') : t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
